import { useEffect, useRef, useState } from "react";
import { Button, DatePicker, Input, Select, Space } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import {
  ACCOUNT_MANAGEMENT_ORDER_STATUSES,
  ACCOUNT_MANAGEMENT_ORDER_STATUS_LABELS,
  IAccountManagementProfitLossFilter,
} from "../../types/AccountManagementCustomer";
import {
  formatIsoDate,
  matchesPreset,
  presetToRange,
  QUICK_DATE_PRESETS,
  QuickDatePreset,
} from "./pnlFilterUtils";
import "./PnlFilterBar.css";

const { RangePicker } = DatePicker;

export interface IListPeriodFilter {
  periodStart?: string;
  periodEnd?: string;
}

type PnlFilterBarProps =
  | {
      mode: "list";
      filter: IListPeriodFilter;
      onChange: (next: IListPeriodFilter) => void;
    }
  | {
      mode: "detail";
      filter: IAccountManagementProfitLossFilter;
      onChange: (next: IAccountManagementProfitLossFilter) => void;
      countries?: string[];
      shipmentStatuses?: string[];
    };

const PnlFilterBar = (props: PnlFilterBarProps) => {
  const detailFilter = props.mode === "detail" ? props.filter : null;
  const detailOnChange = props.mode === "detail" ? props.onChange : null;
  const [searchDraft, setSearchDraft] = useState(detailFilter?.search ?? "");
  const filterRef = useRef(detailFilter);
  filterRef.current = detailFilter;

  useEffect(() => {
    if (props.mode === "detail") {
      setSearchDraft(props.filter.search ?? "");
    }
  }, [props.mode, props.mode === "detail" ? props.filter.search : undefined]);

  useEffect(() => {
    if (props.mode !== "detail" || !detailOnChange) return;
    const timer = setTimeout(() => {
      const trimmed = searchDraft.trim();
      const current = filterRef.current;
      if (!current || (current.search ?? "") === trimmed) return;
      detailOnChange({ ...current, search: trimmed || undefined });
    }, 350);
    return () => clearTimeout(timer);
  }, [searchDraft, props.mode, detailOnChange]);

  const applyQuickPreset = (preset: QuickDatePreset) => {
    const [from, to] = presetToRange(preset);
    if (props.mode === "list") {
      props.onChange({
        ...props.filter,
        periodStart: formatIsoDate(from),
        periodEnd: formatIsoDate(to),
      });
    } else {
      props.onChange({
        ...props.filter,
        purchaseDateFrom: formatIsoDate(from),
        purchaseDateTo: formatIsoDate(to),
      });
    }
  };

  const dateFrom =
    props.mode === "list" ? props.filter.periodStart : props.filter.purchaseDateFrom;
  const dateTo = props.mode === "list" ? props.filter.periodEnd : props.filter.purchaseDateTo;

  const rangeValue: [Dayjs | null, Dayjs | null] | null =
    dateFrom || dateTo
      ? [dateFrom ? dayjs(dateFrom) : null, dateTo ? dayjs(dateTo) : null]
      : null;

  const handleRangeChange = (values: [Dayjs | null, Dayjs | null] | null) => {
    const from = formatIsoDate(values?.[0]);
    const to = formatIsoDate(values?.[1]);
    if (props.mode === "list") {
      props.onChange({ ...props.filter, periodStart: from, periodEnd: to });
    } else {
      props.onChange({ ...props.filter, purchaseDateFrom: from, purchaseDateTo: to });
    }
  };

  const clearDates = () => {
    if (props.mode === "list") {
      props.onChange({ ...props.filter, periodStart: undefined, periodEnd: undefined });
    } else {
      props.onChange({ ...props.filter, purchaseDateFrom: undefined, purchaseDateTo: undefined });
    }
  };

  return (
    <div className="pnl-filter-bar">
      <div className="pnl-filter-row">
        <span className="pnl-filter-label">Tarih</span>
        <Space wrap size={[6, 6]}>
          {QUICK_DATE_PRESETS.map(({ key, label }) => (
            <Button
              key={key}
              size="small"
              type={matchesPreset(key, dateFrom, dateTo) ? "primary" : "default"}
              className="pnl-filter-chip-btn"
              onClick={() => applyQuickPreset(key)}
            >
              {label}
            </Button>
          ))}
          <RangePicker
            size="small"
            className="pnl-filter-range"
            value={rangeValue}
            onChange={handleRangeChange}
            format="DD/MM/YYYY"
            allowEmpty={[true, true]}
            placeholder={["Başlangıç", "Bitiş"]}
          />
          {(dateFrom || dateTo) && (
            <Button size="small" type="link" onClick={clearDates}>
              Temizle
            </Button>
          )}
        </Space>
      </div>

      {props.mode === "detail" && (
        <div className="pnl-filter-row pnl-filter-row--secondary">
          <Input
            size="small"
            allowClear
            prefix={<SearchOutlined />}
            className="pnl-filter-search"
            placeholder="Sipariş, ASIN veya Shipment No…"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
          />
          <Select
            size="small"
            allowClear
            placeholder="Sipariş Durumu"
            className="pnl-filter-select"
            value={props.filter.orderStatus}
            onChange={(v) => props.onChange({ ...props.filter, orderStatus: v })}
            options={ACCOUNT_MANAGEMENT_ORDER_STATUSES.map((s) => ({
              value: s,
              label: ACCOUNT_MANAGEMENT_ORDER_STATUS_LABELS[s],
            }))}
          />
          <Select
            size="small"
            allowClear
            placeholder="Kargo Durumu"
            className="pnl-filter-select"
            value={props.filter.shipmentStatus}
            onChange={(v) => props.onChange({ ...props.filter, shipmentStatus: v })}
            options={(props.shipmentStatuses ?? []).map((s) => ({ value: s, label: s }))}
          />
          <Select
            size="small"
            allowClear
            placeholder="Pazar"
            className="pnl-filter-select"
            value={props.filter.country}
            onChange={(v) => props.onChange({ ...props.filter, country: v })}
            options={(props.countries ?? []).map((c) => ({ value: c, label: c }))}
          />
          <Select
            size="small"
            allowClear
            placeholder="Not"
            className="pnl-filter-select pnl-filter-select--narrow"
            value={
              props.filter.hasNote === undefined
                ? undefined
                : props.filter.hasNote
                  ? "yes"
                  : "no"
            }
            onChange={(v) =>
              props.onChange({
                ...props.filter,
                hasNote: v === undefined ? undefined : v === "yes",
              })
            }
            options={[
              { value: "yes", label: "Not var" },
              { value: "no", label: "Not yok" },
            ]}
          />
        </div>
      )}
    </div>
  );
};

export default PnlFilterBar;
